import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthOptional } from '../lib/auth-context';
import { useT } from '../i18n/locale-context';
import {
  listWatches,
  createWatch,
  deleteWatch,
  type OrgWatch,
  type OrgWatchKind,
} from '../lib/watch-client';
import { ApiError } from '../lib/api';

/**
 * Follow an organisation's awards (ADR 0009), placed on the org profile pages.
 *
 * On the profile and not in a settings screen, because this is the one place
 * the ИНН is already in the reader's hands. V1 takes a TIN and nothing else —
 * matching a typed company name against 39,138 organisations is its own
 * problem, which the ADR keeps out of scope — so any entry point that is not
 * already looking at an org would have to ask for nine digits cold.
 */
export function WatchButton({
  tin,
  kind,
  label,
  compact = false,
}: {
  tin: string;
  kind: OrgWatchKind;
  label?: string | null;
  /** Drop the standing digest hint so the button can sit inline beside another
   *  control (e.g. the claim link on the buyer header). Refusal messages
   *  (pro_required / watch_limit) still render — they answer the click. */
  compact?: boolean;
}) {
  const auth = useAuthOptional();
  const t = useT();
  const qc = useQueryClient();
  const signedIn = auth?.status === 'authed';

  const { data: watches } = useQuery({
    queryKey: ['watches'],
    queryFn: listWatches,
    enabled: signedIn,
  });
  const existing = watches?.find((w) => w.tin === tin && w.kind === kind);

  // Optimistic on both sides: the button flips the moment it is clicked and
  // only reverts if the server refuses. It used to wait for the request AND a
  // full refetch of the watch list before changing state, which on a slow link
  // read as "nothing happened" until a page reload (Igor, 2026-09-06).
  const add = useMutation({
    mutationFn: () => createWatch({ tin, kind, label: label ?? undefined }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['watches'] });
      const previous = qc.getQueryData<OrgWatch[]>(['watches']);
      qc.setQueryData<OrgWatch[]>(['watches'], (old) => [
        ...(old ?? []),
        {
          id: `optimistic:${kind}:${tin}`,
          tin,
          kind,
          label: label ?? null,
          active: true,
          createdAt: new Date().toISOString(),
        },
      ]);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['watches'], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['watches'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteWatch(id),
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ['watches'] });
      const previous = qc.getQueryData<OrgWatch[]>(['watches']);
      qc.setQueryData<OrgWatch[]>(['watches'], (old) => (old ?? []).filter((w) => w.id !== id));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['watches'], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['watches'] }),
  });

  // Nothing to offer a signed-out reader: a watch belongs to an account and the
  // alert arrives in their digest.
  if (!signedIn) return null;

  const busy = add.isPending || remove.isPending;

  /*
   * The API is the authority on both refusals; the UI surfaces them rather than
   * duplicating the rules and risking the two disagreeing.
   *
   * Branch on the machine-readable CODE, not the message. The previous version
   * matched `message.includes('Pro')` — English server prose used as a
   * control-flow key — which silently swallowed the other refusal entirely:
   * the cap throws `watch_limit` with "Too many watched organisations.", no
   * "Pro" in it, so the component fell through to its normal hint and promised
   * a digest that would never arrive. The click did nothing and said nothing.
   */
  const refusal = add.error instanceof ApiError ? add.error.code : undefined;
  const proRequired = refusal === 'pro_required';
  const limitReached = refusal === 'watch_limit';

  return (
    <div className="flex flex-col gap-1.5">
      {/*
       * ⚠️ Styled for the BRAND-TEAL header, not the bone surface.
       *
       * Both call sites wrap this in `<section className="bg-brand">` (the
       * buyer and supplier profile headers). The original light-surface tokens
       * collapsed against it:
       *
       *   following label  text-brand     #0a5c5c on #0a5c5c  ->  1.0:1
       *   hint text        text-ink-muted #706c84 on #0a5c5c  -> ~1.5:1
       *   idle button      bg-brand       #0a5c5c on #0a5c5c  ->  invisible
       *
       * `ink-muted` is documented in the Tailwind config as tuned to 4.66:1
       * against the BONE page background — which is precisely why it fails on
       * teal. The replacements follow the on-brand idiom already used by the
       * debarred pill on the supplier header: white ink, white-alpha fills.
       *
       * If this component is ever placed on a light surface, it needs a
       * surface prop rather than a tweak — the two palettes cannot be one.
       */}
      <button
        type="button"
        disabled={busy}
        onClick={() => (existing ? remove.mutate(existing.id) : add.mutate())}
        className={
          existing
            ? // Following: a quiet outline, legible on teal. Matches the
              // debarred pill's `border-white/25 bg-white/10 text-white`.
              'inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-white/40 bg-white/10 px-4 text-[14px] font-semibold text-white transition-colors hover:bg-white/20 disabled:opacity-60'
            : // Idle: the primary action. On a teal header the way to make a
              // button advance is to invert it, not to tint it.
              //
              // Hover goes to `bone` (#f7f6f3) rather than `white/90`. An alpha
              // hover lets the teal header bleed through, so the control drifts
              // TOWARD its background on hover — backwards for an affordance,
              // and it reads as washed out rather than pressed. `bone` is the
              // palette's own near-white and stays fully opaque.
              'inline-flex h-10 items-center justify-center gap-1.5 rounded-md bg-white px-4 text-[14px] font-bold text-brand shadow-m-sm transition-colors hover:bg-bone disabled:opacity-60'
        }
      >
        {/* Heart fills once following, outline while idle. */}
        <svg
          width="15"
          height="15"
          viewBox="0 0 16 16"
          fill={existing ? 'currentColor' : 'none'}
          aria-hidden
        >
          <path
            d="M8 2.7C6.9 1.1 4.4 1.2 3.3 3 2.3 4.8 3 6.7 4.4 8L8 11.6 11.6 8C13 6.7 13.7 4.8 12.7 3 11.6 1.2 9.1 1.1 8 2.7Z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </svg>
        {existing ? t('watch.following') : t('watch.follow')}
      </button>

      {proRequired || limitReached ? (
        // A refusal reads at full white — it is the answer to what the reader
        // just did, not ambient help text.
        <p className="text-[12px] font-semibold text-white">
          {limitReached ? t('watch.limitReached') : t('watch.proRequired')}
        </p>
      ) : compact ? null : (
        <p className="text-[12px] text-white/70">
          {kind === 'winner' ? t('watch.hintWinner') : t('watch.hintBuyer')}
        </p>
      )}
    </div>
  );
}
